// Gradient controller fields. Each channel (size, spacing, angle) is a scalar
// field over the canvas: the channel base value, locally pulled toward each
// controller's target value inside its influence radius.

import type { AssetMap, Controller, ControllerSet, DocumentModel, ChannelId, LumaGrid } from './types'
import { MIN_SPACING } from './types'
import { controllerSpine, spineDistSide } from './curves'

function falloff(kind: Controller['falloff'], t: number): number {
  switch (kind) {
    case 'linear':
      return t
    case 'smooth':
      return t * t * (3 - 2 * t)
    case 'sharp':
      return t * t
    case 'soft':
      return Math.sqrt(t)
  }
}

function controllerWeight(c: Controller, x: number, y: number): number {
  const { d, side } = spineDistSide(controllerSpine(c), x, y, c.infinite && c.kind !== 'point')
  // Single side: points strictly on the inactive side get no influence.
  // 'left' is negative side (walking a -> b in the y-down frame), 'right'
  // positive; the spine itself (side 0) always receives influence.
  if (c.kind !== 'point' && c.singleSide !== 'off') {
    if (c.singleSide === 'left' ? side > 0 : side < 0) return 0
  }
  if (c.hardEdge) return d <= c.radius ? 1 : 0
  if (d >= c.radius) return 0
  return falloff(c.falloff, 1 - d / c.radius)
}

// Bilinear luminance lookup at normalized coordinates, clamped at the edges.
export function sampleLuma(grid: LumaGrid, u: number, v: number): number {
  const fx = Math.min(Math.max(u, 0), 1) * (grid.w - 1)
  const fy = Math.min(Math.max(v, 0), 1) * (grid.h - 1)
  const x0 = Math.floor(fx)
  const y0 = Math.floor(fy)
  const x1 = Math.min(x0 + 1, grid.w - 1)
  const y1 = Math.min(y0 + 1, grid.h - 1)
  const tx = fx - x0
  const ty = fy - y0
  const a = grid.data[y0 * grid.w + x0]
  const b = grid.data[y0 * grid.w + x1]
  const c = grid.data[y1 * grid.w + x0]
  const d = grid.data[y1 * grid.w + x1]
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty
}

// Contrast curve around mid-gray: -1 flattens toward 0.5, 0 is identity,
// +1 approaches a threshold. Monotonic for every setting.
export function applyContrast(lum: number, contrast: number): number {
  const c = Math.min(0.999, Math.max(-0.999, contrast))
  const k = Math.tan(((c + 1) * Math.PI) / 4)
  return Math.min(1, Math.max(0, 0.5 + (lum - 0.5) * k))
}

// The image controller's value at (x, y), or null outside its rect.
function imageValue(set: ControllerSet, x: number, y: number, grid: LumaGrid | undefined): number | null {
  const img = set.image
  if (!img || !img.enabled || !grid) return null
  const { rect } = img
  if (rect.w <= 0 || rect.h <= 0) return null
  const u = (x - rect.x) / rect.w
  const v = (y - rect.y) / rect.h
  if (u < 0 || u > 1 || v < 0 || v > 1) return null
  let lum = sampleLuma(grid, u, v)
  if (img.invert) lum = 1 - lum
  lum = applyContrast(lum, img.contrast)
  return img.valueLo + (img.valueHi - img.valueLo) * lum
}

// Combination rule (kept consistent everywhere): weighted blend of controller
// targets by influence, mixed with the base by the strongest influence. An
// image controller contributes with weight 1 inside its rect.
export function evalField(set: ControllerSet, x: number, y: number, grid?: LumaGrid): number {
  const active = set.controllers
  let sumW = 0
  let sumWV = 0
  let maxW = 0
  for (const c of active) {
    if (!c.enabled) continue
    const w = controllerWeight(c, x, y)
    if (w <= 0) continue
    sumW += w
    sumWV += w * c.value
    if (w > maxW) maxW = w
  }
  const iv = imageValue(set, x, y, grid)
  if (iv !== null) {
    sumW += 1
    sumWV += iv
    maxW = 1
  }
  if (sumW <= 0) return set.base
  const blended = sumWV / sumW
  return set.base + (blended - set.base) * maxW
}

export interface FieldSampler {
  size(x: number, y: number): number
  spacing(x: number, y: number): number
  angle(x: number, y: number): number // degrees
  shape(x: number, y: number): number // morph amount, clamped 0..1
}

export function makeSampler(doc: DocumentModel, assets: AssetMap = {}): FieldSampler {
  const get = (id: ChannelId) => doc.channels[id]
  const grid = (id: ChannelId): LumaGrid | undefined => {
    const img = doc.channels[id].image
    return img && img.enabled ? assets[img.dataUri] : undefined
  }
  const gSize = grid('size')
  const gSpacing = grid('spacing')
  const gAngle = grid('angle')
  const gShape = grid('shape')
  return {
    size: (x, y) => Math.max(0.05, evalField(get('size'), x, y, gSize)),
    spacing: (x, y) => Math.max(MIN_SPACING, evalField(get('spacing'), x, y, gSpacing)),
    angle: (x, y) => evalField(get('angle'), x, y, gAngle),
    shape: (x, y) => Math.min(1, Math.max(0, evalField(get('shape'), x, y, gShape))),
  }
}
